import { z } from 'zod';
import type { ApiContract } from '../contracts';
import {
  AssignableRoleSchema,
  InviteRoleSchema,
  OrganizationRoleSchema,
  type OrganizationRole,
} from '../roles';

export const CreateOrganizationRequestSchema = z.object({
  name: z.string().trim().min(1).max(100),
});
export type CreateOrganizationRequest = z.infer<typeof CreateOrganizationRequestSchema>;

/**
 * The caller's organization role (admin | editor | viewer). Canonical vocabulary
 * lives in `common/roles.ts`; re-exported here under the historical name so the
 * org contracts have one import surface.
 */
export const OrganizationMemberRoleSchema = OrganizationRoleSchema;
export type OrganizationMemberRoleValue = OrganizationRole;

export const OrganizationSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});
export type OrganizationSummary = z.infer<typeof OrganizationSummarySchema>;

export const CreateOrganizationResponseSchema = z.object({
  organization: OrganizationSummarySchema,
});
export type CreateOrganizationResponse = z.infer<typeof CreateOrganizationResponseSchema>;

export const createOrganizationContract = {
  method: 'POST',
  path: '/api/organizations',
  body: CreateOrganizationRequestSchema,
  response: CreateOrganizationResponseSchema,
} as const satisfies ApiContract;

export const GetCurrentOrganizationResponseSchema = z.object({
  organization: OrganizationSummarySchema.nullable(),
  // The caller's membership role in the current organization, or null when
  // they have no membership. Sibling to `organization` (not a property of the
  // org) because it describes the caller, not the org. Used by client role
  // gating (the `useRole` hook).
  role: OrganizationMemberRoleSchema.nullable(),
});
export type GetCurrentOrganizationResponse = z.infer<typeof GetCurrentOrganizationResponseSchema>;

export const getCurrentOrganizationContract = {
  method: 'GET',
  path: '/api/organizations/current',
  response: GetCurrentOrganizationResponseSchema,
} as const satisfies ApiContract;

// Renames the caller's current organization (not a create). Admin-only.
export const SetOrganizationNameRequestSchema = z.object({
  name: z.string().trim().min(1).max(100),
});
export type SetOrganizationNameRequest = z.infer<typeof SetOrganizationNameRequestSchema>;

export const SetOrganizationNameResponseSchema = z.object({
  organization: OrganizationSummarySchema,
});
export type SetOrganizationNameResponse = z.infer<typeof SetOrganizationNameResponseSchema>;

export const setOrganizationNameContract = {
  method: 'POST',
  path: '/api/organizations/name',
  body: SetOrganizationNameRequestSchema,
  response: SetOrganizationNameResponseSchema,
} as const satisfies ApiContract;

// ── Organization switching ──────────────────────────────────────────────────

/** One organization the caller belongs to, with their role in it (org switcher). */
export const MyOrganizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  role: OrganizationMemberRoleSchema,
});
export type MyOrganization = z.infer<typeof MyOrganizationSchema>;

export const ListMyOrganizationsResponseSchema = z.object({
  organizations: z.array(MyOrganizationSchema),
  /** The currently active organization id (null only mid-onboarding). */
  activeOrganizationId: z.string().uuid().nullable(),
});
export type ListMyOrganizationsResponse = z.infer<typeof ListMyOrganizationsResponseSchema>;

export const listMyOrganizationsContract = {
  method: 'GET',
  path: '/api/organizations/mine',
  response: ListMyOrganizationsResponseSchema,
} as const satisfies ApiContract;

/** Switches the caller's active organization (org switcher menu). Membership-verified. */
export const SwitchOrganizationRequestSchema = z.object({
  organizationId: z.string().uuid(),
});
export type SwitchOrganizationRequest = z.infer<typeof SwitchOrganizationRequestSchema>;

export const SwitchOrganizationResponseSchema = z.object({
  organization: OrganizationSummarySchema,
  role: OrganizationMemberRoleSchema,
});
export type SwitchOrganizationResponse = z.infer<typeof SwitchOrganizationResponseSchema>;

export const switchOrganizationContract = {
  method: 'POST',
  path: '/api/organizations/switch',
  body: SwitchOrganizationRequestSchema,
  response: SwitchOrganizationResponseSchema,
} as const satisfies ApiContract;

// ── Settings → Team ────────────────────────────────────────────────────────

/** One active member row in the Team list — identity + role. */
export const TeamMemberSchema = z.object({
  userId: z.string().uuid(),
  name: z.string(),
  email: z.string(),
  role: OrganizationMemberRoleSchema,
});
export type TeamMember = z.infer<typeof TeamMemberSchema>;

/** One pending (not-yet-accepted) invite row in the Team list. */
export const TeamInviteSchema = z.object({
  email: z.string(),
  name: z.string().nullable(),
  role: OrganizationMemberRoleSchema,
});
export type TeamInvite = z.infer<typeof TeamInviteSchema>;

/**
 * The Settings → Team read: active members + pending invites. Any member may
 * read it; the mutations below are admin-only. Returned by every team mutation
 * too, so the panel re-renders from one round trip.
 */
export const TeamResponseSchema = z.object({
  members: z.array(TeamMemberSchema),
  invites: z.array(TeamInviteSchema),
});
export type TeamResponse = z.infer<typeof TeamResponseSchema>;

export const listTeamMembersContract = {
  method: 'GET',
  path: '/api/organizations/members',
  response: TeamResponseSchema,
} as const satisfies ApiContract;

/**
 * The roles an admin may assign to another member (Team role change) — any of
 * the three tiers ("change anyone's role to anything"). The server refuses
 * changing your own role and demoting the last remaining admin.
 */
export const SettableMemberRoleSchema = AssignableRoleSchema;
export type SettableMemberRole = OrganizationRole;

export const SetMemberRoleRequestSchema = z.object({
  userId: z.string().uuid(),
  role: SettableMemberRoleSchema,
});
export type SetMemberRoleRequest = z.infer<typeof SetMemberRoleRequestSchema>;

export const setMemberRoleContract = {
  method: 'POST',
  path: '/api/organizations/member-role',
  body: SetMemberRoleRequestSchema,
  response: TeamResponseSchema,
} as const satisfies ApiContract;

/** Removes a member from the org (admin-only). The user + their work are retained. */
export const RemoveMemberRequestSchema = z.object({
  userId: z.string().uuid(),
});
export type RemoveMemberRequest = z.infer<typeof RemoveMemberRequestSchema>;

export const removeMemberContract = {
  method: 'POST',
  path: '/api/organizations/remove-member',
  body: RemoveMemberRequestSchema,
  response: TeamResponseSchema,
} as const satisfies ApiContract;

/** Invites one teammate by name + email at a non-admin role (admin-only). */
export const InviteTeamMemberRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().pipe(z.email()),
  role: InviteRoleSchema,
});
export type InviteTeamMemberRequest = z.infer<typeof InviteTeamMemberRequestSchema>;

export const inviteTeamMemberContract = {
  method: 'POST',
  path: '/api/organizations/invite',
  body: InviteTeamMemberRequestSchema,
  response: TeamResponseSchema,
} as const satisfies ApiContract;

// ── IP allowlist (Security settings) ────────────────────────────────────────
//
// An org's IP allowlist restricts authenticated traffic (pages + org-scoped API)
// to a set of IPv4/IPv6 addresses and CIDR ranges. Admin-only to view/manage.
// The IP/CIDR parsing + matching lives in the shared, pure `common/ip-allowlist.ts`
// so the settings form validates with the exact logic the server enforces with.

/** One stored rule as served: the canonical CIDR, its family, and an optional note. */
export const IpRuleSchema = z.object({
  cidr: z.string(),
  family: z.enum(['v4', 'v6']),
  label: z.string().nullable(),
});
export type IpRuleView = z.infer<typeof IpRuleSchema>;

/** One rule as submitted: the raw IP/CIDR the admin typed + an optional note. The
 *  server canonicalizes `value` via `parseIpRule` (never trusting the client). */
export const IpRuleInputSchema = z.object({
  value: z.string().trim().min(1).max(64),
  label: z.string().trim().max(80).nullable().default(null),
});
export type IpRuleInput = z.infer<typeof IpRuleInputSchema>;

export const IpAllowlistResponseSchema = z.object({
  rules: z.array(IpRuleSchema),
});
export type IpAllowlistResponse = z.infer<typeof IpAllowlistResponseSchema>;

/** Reads the org's IP allowlist (admin-only). */
export const getIpAllowlistContract = {
  method: 'GET',
  path: '/api/organizations/ip-allowlist',
  response: IpAllowlistResponseSchema,
} as const satisfies ApiContract;

/** Replaces the org's entire IP allowlist (admin-only, full-set save). The server
 *  rejects a save that would lock the calling admin out (their current IP not
 *  covered) with a 409. */
export const SetIpAllowlistRequestSchema = z.object({
  rules: z.array(IpRuleInputSchema).max(200),
});
export type SetIpAllowlistRequest = z.infer<typeof SetIpAllowlistRequestSchema>;

export const setIpAllowlistContract = {
  method: 'POST',
  path: '/api/organizations/ip-allowlist',
  body: SetIpAllowlistRequestSchema,
  response: IpAllowlistResponseSchema,
} as const satisfies ApiContract;

/** Reports whether the CURRENT request's client IP passes the active org's
 *  allowlist. Returns the client's own IP (for the blocked page to show) and
 *  whether an allowlist exists — never the rule list itself. Reachable even when
 *  blocked (it does not enforce the allowlist), so the blocked page can read it. */
export const IpAccessStatusResponseSchema = z.object({
  hasAllowlist: z.boolean(),
  allowed: z.boolean(),
  clientIp: z.string().nullable(),
  // Support contact for the blocked page's mailto link (server-resolved from env,
  // so no public env var needs threading to the client).
  supportEmail: z.string(),
});
export type IpAccessStatusResponse = z.infer<typeof IpAccessStatusResponseSchema>;

export const getIpAccessStatusContract = {
  method: 'GET',
  path: '/api/organizations/ip-access-status',
  response: IpAccessStatusResponseSchema,
} as const satisfies ApiContract;

// ── Pending invitations (existing accounts) ─────────────────────────────────
//
// Invites are matched by email. A fresh sign-up is auto-accepted during the
// verify-email onboarding step, but an EXISTING (already-verified) account
// never passes through that step — these contracts give signed-in users an
// explicit surface to see and accept/decline invites addressed to their email.
// Nothing auto-joins silently outside onboarding.

/** One pending invitation addressed to the caller's (verified) email. */
export const PendingInvitationSchema = z.object({
  organizationId: z.string().uuid(),
  organizationName: z.string(),
  /** Display name of the admin who sent the invite. Null for legacy rows. */
  inviterName: z.string().nullable(),
  /** Role the caller would be admitted at on accept. */
  role: OrganizationMemberRoleSchema,
});
export type PendingInvitation = z.infer<typeof PendingInvitationSchema>;

export const ListMyInvitationsResponseSchema = z.object({
  invitations: z.array(PendingInvitationSchema),
});
export type ListMyInvitationsResponse = z.infer<typeof ListMyInvitationsResponseSchema>;

export const listMyInvitationsContract = {
  method: 'GET',
  path: '/api/organizations/invitations',
  response: ListMyInvitationsResponseSchema,
} as const satisfies ApiContract;

/**
 * Accepts one pending invitation (keyed by org — invites are unique per
 * org + email). Creates the membership at the invited role and switches the
 * caller's active organization to it, exactly like the onboarding auto-accept.
 */
export const AcceptInvitationRequestSchema = z.object({
  organizationId: z.string().uuid(),
});
export type AcceptInvitationRequest = z.infer<typeof AcceptInvitationRequestSchema>;

export const AcceptInvitationResponseSchema = z.object({
  organization: OrganizationSummarySchema,
  role: OrganizationMemberRoleSchema,
});
export type AcceptInvitationResponse = z.infer<typeof AcceptInvitationResponseSchema>;

export const acceptInvitationContract = {
  method: 'POST',
  path: '/api/organizations/invitations/accept',
  body: AcceptInvitationRequestSchema,
  response: AcceptInvitationResponseSchema,
} as const satisfies ApiContract;

/**
 * Declines one pending invitation — deletes the invite row, creates nothing.
 * Returns the caller's remaining pending invitations so the surface re-renders
 * from one round trip (mirrors the Team mutations returning the team list).
 */
export const DeclineInvitationRequestSchema = z.object({
  organizationId: z.string().uuid(),
});
export type DeclineInvitationRequest = z.infer<typeof DeclineInvitationRequestSchema>;

export const declineInvitationContract = {
  method: 'POST',
  path: '/api/organizations/invitations/decline',
  body: DeclineInvitationRequestSchema,
  response: ListMyInvitationsResponseSchema,
} as const satisfies ApiContract;
